export const supportedLocales = ['nl', 'en', 'fr', 'es', 'de'] as const

export type AppLocale = (typeof supportedLocales)[number]

let preferredLocale: AppLocale | null = null
const LOCALE_STORAGE_KEY = 'golero_locale'

function normalizeLocale(candidate: string | null | undefined): AppLocale | null {
  if (!candidate) return null

  const normalized = candidate.toLowerCase().split('-')[0]
  return supportedLocales.includes(normalized as AppLocale) ? (normalized as AppLocale) : null
}

export function getLocale(): AppLocale {
  if (preferredLocale) return preferredLocale

  if (typeof window !== 'undefined') {
    const storedLocale = normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY))
    if (storedLocale) {
      preferredLocale = storedLocale
      return storedLocale
    }
  }

  const candidates = [
    ...(typeof navigator !== 'undefined' ? navigator.languages : []),
    typeof navigator !== 'undefined' ? navigator.language : '',
    typeof document !== 'undefined' ? document.documentElement.lang : '',
  ]

  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate)
    if (locale) return locale
  }

  return 'nl'
}

export function setPreferredLocale(candidate: string | null | undefined) {
  const nextLocale = normalizeLocale(candidate)
  if (preferredLocale === nextLocale) return false
  preferredLocale = nextLocale
  if (typeof window !== 'undefined') {
    if (nextLocale) window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale)
    else window.localStorage.removeItem(LOCALE_STORAGE_KEY)
    window.dispatchEvent(new CustomEvent('golero:locale-changed', { detail: nextLocale }))
  }
  return true
}

export function syncDocumentLocale() {
  if (typeof document === 'undefined') return
  document.documentElement.lang = getLocale()
}

export function toIntlLocale(locale?: string) {
  switch (locale) {
    case 'en':
      return 'en-GB'
    case 'fr':
      return 'fr-FR'
    case 'es':
      return 'es-ES'
    case 'de':
      return 'de-DE'
    case 'nl':
    default:
      return 'nl-NL'
  }
}

export function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam'
}
